import { IVector4, IColor4 } from '../../api.js';
export class MeshDescriptions {
    constructor() {
        throw new Error('This is an utility class, cannot be instantiated');
    }
    static box(width = 1, height = 1, depth = 1, color = new IColor4(1, 1, 1, 1)) {
        const half_width = width / 2;
        const half_height = height / 2;
        const half_depth = depth / 2;
        const positions = [
            // Front face
            -half_width, -half_height, half_depth,
            +half_width, -half_height, half_depth,
            +half_width, +half_height, half_depth,
            -half_width, +half_height, half_depth,
            // Back face
            +half_width, -half_height, -half_depth,
            -half_width, -half_height, -half_depth,
            -half_width, +half_height, -half_depth,
            +half_width, +half_height, -half_depth,
            // Top face
            -half_width, +half_height, +half_depth,
            +half_width, +half_height, +half_depth,
            +half_width, +half_height, -half_depth,
            -half_width, +half_height, -half_depth,
            // Bottom face
            -half_width, -half_height, -half_depth,
            +half_width, -half_height, -half_depth,
            +half_width, -half_height, +half_depth,
            -half_width, -half_height, +half_depth,
            // Right face
            +half_width, -half_height, +half_depth,
            +half_width, -half_height, -half_depth,
            +half_width, +half_height, -half_depth,
            +half_width, +half_height, +half_depth,
            // Left face
            -half_width, -half_height, -half_depth,
            -half_width, -half_height, +half_depth,
            -half_width, +half_height, +half_depth,
            -half_width, +half_height, -half_depth
        ];
        const indices = [
            +0, +1, +2, +0, +2, +3, // Front face
            +4, +5, +6, +4, +6, +7, // Back face
            +8, +9, 10, +8, 10, 11, // Top face
            12, 13, 14, 12, 14, 15, // Bottom face
            16, 17, 18, 16, 18, 19, // Right face
            20, 21, 22, 20, 22, 23 // Left face
        ];
        const colors = [];
        for (let i = 0; i < 24; i++) { // One color per each vertex
            colors.push(color.r, color.g, color.b, color.a);
        }
        const uv = [
            0, 1, 1, 1, 1, 0, 0, 0,
            0, 1, 1, 1, 1, 0, 0, 0,
            1, 0, 0, 0, 0, 1, 1, 1,
            1, 0, 0, 0, 0, 1, 1, 1,
            0, 1, 1, 1, 1, 0, 0, 0,
            0, 1, 1, 1, 1, 0, 0, 0
        ];
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors),
            uv0: new Float32Array(uv)
        };
    }
    static sphere(diameter = 1.0, segments = 32, smooth = true, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const indices = [];
        const normals = [];
        const colors = [];
        const uv = [];
        const radius = diameter / 2;
        for (let i = 0; i <= segments; i++) {
            const latitude = i * Math.PI / segments;
            for (let j = 0; j <= segments; j++) {
                const longitude = j * 2 * Math.PI / segments;
                const x = Math.sin(latitude) * Math.cos(longitude);
                const y = Math.cos(latitude);
                const z = Math.sin(latitude) * Math.sin(longitude);
                positions.push(x * radius, y * radius, z * radius);
                normals.push(x, y, z);
                uv.push(-j / segments, i / segments);
                colors.push(color.r, color.g, color.b, color.a);
            }
        }
        for (let i = 0; i < segments; i++) {
            for (let j = 0; j < segments; j++) {
                const first = (i * (segments + 1)) + j;
                const second = first + segments + 1;
                indices.push(first, first + 1, second);
                indices.push(second, first + 1, second + 1);
            }
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            normals: smooth ? new Float32Array(normals) : null,
            colors: new Float32Array(colors),
            uv0: new Float32Array(uv)
        };
    }
    static spherical_dome(diameter = 1.0, cutoff = 0.5, segments = 32, smooth = true, flip = false, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const indices = [];
        const normals = [];
        const colors = [];
        const uv = [];
        const radius = diameter / 2;
        const vertical_segments = Math.ceil(segments * cutoff);
        for (let i = 0; i <= vertical_segments; i++) {
            const alpha = i / vertical_segments;
            const latitude = (flip ? 1 - (alpha * cutoff) : (alpha * cutoff)) * Math.PI;
            for (let j = 0; j <= segments; j++) {
                const longitude = j * 2 * Math.PI / segments;
                const x = Math.sin(latitude) * Math.cos(longitude);
                const y = Math.cos(latitude);
                const z = Math.sin(latitude) * Math.sin(longitude);
                positions.push(x * radius, y * radius, z * radius);
                normals.push(x, y, z);
                uv.push(-j / segments, i / vertical_segments);
                colors.push(color.r, color.g, color.b, color.a);
            }
        }
        for (let i = 0; i < vertical_segments; i++) {
            for (let j = 0; j < segments; j++) {
                const first = (i * (segments + 1)) + j;
                const second = first + segments + 1;
                indices.push(first, first + 1, second);
                indices.push(second, first + 1, second + 1);
            }
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(flip ? indices.reverse() : indices),
            normals: smooth ? new Float32Array(normals) : null,
            colors: new Float32Array(colors),
            uv0: new Float32Array(uv)
        };
    }
    static plane(width = 1, depth = 1, uv0 = new IVector4(0, 0, 1, 1), color = new IColor4(1, 1, 1, 1)) {
        const half_width = width / 2;
        const half_height = depth / 2;
        const positions = [
            -half_width, +half_height, 0,
            +half_width, +half_height, 0,
            +half_width, -half_height, 0,
            -half_width, -half_height, 0
        ];
        const colors = [
            color.r, color.g, color.b, color.a,
            color.r, color.g, color.b, color.a,
            color.r, color.g, color.b, color.a,
            color.r, color.g, color.b, color.a
        ];
        const uv0_vertex_values = [
            uv0.z, uv0.y,
            uv0.x, uv0.y,
            uv0.x, uv0.w,
            uv0.z, uv0.w
        ];
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array([0, 1, 2, 0, 2, 3]),
            colors: new Float32Array(colors),
            uv0: new Float32Array(uv0_vertex_values)
        };
    }
    static torus(major_radius, minor_radius, major_segments = 32, minor_segments = 16, smooth = true, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const colors = [];
        const indices = [];
        const normals = [];
        const uvs = [];
        for (let i = 0; i <= major_segments; i++) {
            const theta = (i / major_segments) * 2 * Math.PI;
            const sin_theta = Math.sin(theta);
            const cos_theta = Math.cos(theta);
            for (let j = 0; j <= minor_segments; j++) {
                const phi = (j / minor_segments) * 2 * Math.PI;
                const sin_phi = Math.sin(phi);
                const cos_phi = Math.cos(phi);
                const x = (major_radius + minor_radius * cos_phi) * cos_theta;
                const y = minor_radius * sin_phi;
                const z = (major_radius + minor_radius * cos_phi) * sin_theta;
                positions.push(x, y, z);
                colors.push(color.r, color.g, color.b, color.a);
                normals.push(cos_theta * cos_phi, sin_phi, sin_theta * cos_phi);
                uvs.push(i / major_segments, j / minor_segments);
            }
        }
        for (let i = 0; i < major_segments; i++) {
            for (let j = 0; j < minor_segments; j++) {
                const first = (i * (minor_segments + 1)) + j;
                const second = first + minor_segments + 1;
                indices.push(first, first + 1, second);
                indices.push(second, first + 1, second + 1);
            }
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors),
            normals: smooth ? new Float32Array(normals) : null,
            uv0: new Float32Array(uvs)
        };
    }
    static cylinder(height, radius, segments = 32, smooth = true, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const colors = [];
        const indices = [];
        const normals = [];
        const uvs = [];
        const angle_step = (Math.PI * 2) / segments;
        // Generate positions and normals
        for (let i = 0; i < segments; i++) {
            const theta = i * angle_step;
            const cos = Math.cos(theta);
            const sin = Math.sin(theta);
            const x = radius * cos;
            const z = radius * sin;
            positions.push(x, 0, z);
            normals.push(0, -1, 0);
            uvs.push(x, z);
            positions.push(x, height, z);
            normals.push(0, 1, 0);
            uvs.push(x, z);
            positions.push(x, 0, z);
            normals.push(cos, 0, sin);
            uvs.push(i / segments, 0);
            positions.push(x, height, z);
            normals.push(cos, 0, sin);
            uvs.push(i / segments, 1);
        }
        // Top and bottom circles
        for (let i = 0; i < segments - 2; i++) {
            indices.push(0, (i + 1) * 4, (i + 2) * 4);
            indices.push(1, (i + 2) * 4 + 1, (i + 1) * 4 + 1);
        }
        // Side faces
        for (let i = 0; i < segments; i++) {
            indices.push(i * 4 + 2, i * 4 + 3, ((i + 1) * 4 + 2) % (segments * 4));
            indices.push(i * 4 + 3, ((i + 1) * 4 + 2) % (segments * 4) + 1, ((i + 1) * 4 + 2) % (segments * 4));
        }
        for (let i = 0; i < positions.length; i++) {
            colors.push(color.r, color.g, color.b, color.a);
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            colors: new Float32Array(colors),
            normals: smooth ? new Float32Array(normals) : null,
            uv0: new Float32Array(uvs)
        };
    }
    static cone(height, radius, segments = 32, smooth = true, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const indices = [];
        const normals = [];
        const colors = [];
        const uvs = [];
        const angle_step = (Math.PI * 2) / segments;
        // Generate positions and normals for the base circle
        for (let i = 0; i < segments; i++) {
            const theta = i * angle_step;
            const cos = Math.cos(theta);
            const sin = Math.sin(theta);
            const x = radius * cos;
            const z = radius * sin;
            positions.push(x, 0, z);
            normals.push(0, -1, 0);
            uvs.push(x, z);
            positions.push(x, 0, z);
            normals.push(cos, Math.sqrt(x * x + z * z), sin);
            uvs.push(i / segments, 0);
            positions.push(0, height, 0);
            normals.push(cos, Math.sqrt(x * x + z * z), sin);
            uvs.push(i / segments, 1);
        }
        // Bottom
        for (let i = 0; i < segments - 2; i++) {
            indices.push(0, (i + 1) * 3, (i + 2) * 3);
        }
        // Side faces
        for (let i = 0; i < segments; i++) {
            indices.push(i * 3 + 1, i * 3 + 2, (i + 1) * 3 % (segments * 3) + 1);
        }
        for (let i = 0; i < positions.length; i++) {
            colors.push(color.r, color.g, color.b, color.a);
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            normals: smooth ? new Float32Array(normals) : null,
            colors: new Float32Array(colors),
            uv0: new Float32Array(uvs)
        };
    }
    static circle(radius, segments = 32, color = new IColor4(1, 1, 1, 1)) {
        const positions = [];
        const indices = [];
        const normals = [];
        const colors = [];
        const uvs = [];
        const angle_step = (Math.PI * 2) / segments;
        for (let i = 0; i < segments; i++) {
            const x = radius * Math.cos(i * angle_step);
            const z = radius * Math.sin(i * angle_step);
            positions.push(x, 0, z);
            normals.push(0, 1, 0);
            uvs.push(x, z);
        }
        for (let i = 0; i < segments - 2; i++) {
            indices.push(0, i + 2, i + 1);
        }
        for (let i = 0; i < positions.length; i++) {
            colors.push(color.r, color.g, color.b, color.a);
        }
        return {
            positions: new Float32Array(positions),
            indices: new Uint16Array(indices),
            normals: new Float32Array(normals),
            colors: new Float32Array(colors),
            uv0: new Float32Array(uvs)
        };
    }
}
//# sourceMappingURL=MeshDescriptions.js.map